/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : TableDialog.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import {
  Modal,
  Box,
  Typography,
  Paper,
  IconButton,

} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import React, { useState } from "react";
import PropTypes from "prop-types";

import { HandleApiActions, legalRequirements } from "../../redux/actions";
import { useDispatch } from "react-redux";
import CustomFormTextFieldV3 from "../../components/customComponentsV2/common/textField/CustomFormTextFieldV3";
import CustomButtonV2 from "../../components/customComponentsV2/common/button/CustomButtonV2";

function TableDialog({
  openDialogTable,
  setOpenDialogTable,
  selectedLSDetails,
  setSelectedLSTableDetails,
  selectedLSTable,
  refreshLegalStandards,
  fetchTotalTableDetails
}) {
  const dispatch = useDispatch();
  const [table, setTable] = useState({
    title: "",
    description: "",
  });

  const handleCloseDialog = () => {
    setOpenDialogTable(false);
  };

  const isTableComplete =
    !!table?.title?.trim() && !!table?.description?.trim();

  const handleSubmit = async () => {
    const reqObj = {
      ls_id: selectedLSDetails,
      table: table,
      columns: [],
    };
    const resp = await dispatch(
      HandleApiActions({
        ...legalRequirements.add_legal_standard_table,
        params: reqObj,
        show_toast: true,
      }),
    );
    if (!resp?.error) {
      await refreshLegalStandards(false);
      await fetchTotalTableDetails();

      const detailsResp = await dispatch(
        HandleApiActions({
          ...legalRequirements.get_legal_standard_table_details,
          params: {
            ls_id: selectedLSDetails,
            table_id: selectedLSTable,
          },
        }),
      );
      if (detailsResp?.data?.length > 0) {
        setSelectedLSTableDetails(detailsResp?.data[0]?.legal_standard_table || null);
      }
      handleCloseDialog();
    }
  };

  return (
    <Modal open={openDialogTable} onClose={handleCloseDialog}>
      <Box
        sx={{
          position: "fixed",
          inset: 0,
          bgcolor: "rgba(0, 0, 0, 0.5)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1300,
          p: 2,
        }}
      >
        <Paper
          elevation={24}
          sx={{
            borderRadius: 3,
            width: "100%",
            maxWidth: "600px",
            maxHeight: "90vh",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              p: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderBottom: "1px solid #E5E7EB",
            }}
          >
            <Typography variant="h6">Add Table</Typography>
            <IconButton
              onClick={handleCloseDialog}
              sx={{ "&:hover": { color: "#E5E7EB" } }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <Box
            sx={{
              p: 3,
              overflowY: "auto",
              flexGrow: 1,
              display: "flex",
              flexDirection: "column",
              gap: 6,
            }}
          >
            <Box>
              <Box>
                <CustomFormTextFieldV3
                  label="Table Title"
                  size="small"
                  required
                  type="text"
                  fldType="text"
                  value={table.title}
                  setValue={(val) =>
                    setTable((prev) => ({ ...prev, title: val }))
                  }
                  placeholder="Enter Table Title"
                />
              </Box>

              <Box sx={{ mt: 2 }}>
                <CustomFormTextFieldV3
                  isMultiline={true}
                  label="Table Description"
                  size="small"
                  required
                  type="text"
                  fldType="text"
                  value={table.description}
                  setValue={(val) =>
                    setTable((prev) => ({ ...prev, description: val }))
                  }
                  placeholder="Enter Table Description"
                />
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              bgcolor: "#fff",
              px: 4,
              py: 2,
              display: "flex",
              justifyContent: "flex-end",
              borderTop: "1px solid #E5E7EB",
            }}
          >
            <CustomButtonV2
              btnName="Add"
              variant="contained"
              handleOnClick={handleSubmit}
              disabled={!isTableComplete}
            >
              Add
            </CustomButtonV2>
          </Box>
        </Paper>
      </Box>
    </Modal>
  );
}

TableDialog.propTypes = {
  openDialogTable: PropTypes.bool,
  setOpenDialogTable: PropTypes.func,
  selectedLSDetails: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  setSelectedLSTableDetails: PropTypes.func,
  selectedLSTable: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  refreshLegalStandards: PropTypes.func,
  fetchTotalTableDetails: PropTypes.func,
};

export default TableDialog;

